import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { formatAZN, pickLocale } from "../../lib/format";
import { api, mediaUrl, sizedMediaUrl } from "../../lib/api";
import { authStore } from "../../lib/authStore";
import { analytics } from "../../lib/analytics";
import { useTryOnLauncher } from "../../lib/tryonLauncher";
import { useHoverCapable } from "../../hooks/useHoverCapable";
import { useI18n } from "../../lib/i18n";
import { productImage } from "./assets";
import AdfImage from "./AdfImage";
import TryOnBadge from "./TryOnBadge";


/* The card used to run every category name through a 17-entry rewrite table, so
   a shelf an editor named "Suits" rendered as "Kostyumlar" and no amount of
   editing in admin could change it. The catalogue is Azerbaijani already; the
   name the editor typed is the name that shows. */
function translateTag(value) {
  return String(value || "").trim();
}

function categoryLabel(item, locale, t) {
  const raw = (
    pickLocale(item?.category?.title, locale)
    || pickLocale(item?.category_title, locale)
    || item?.category_name
    || item?.category_slug
    || item?.product_type
  );
  return raw ? translateTag(String(raw).replace(/[-_]/g, " "), t) : "";
}

/* Rəng-ayrı-kart merchandising proyeksiyası (sahibin spesifikasiyası,
   2026-09-24). Bayrağı ON olan məhsul stokda olan hər uyğun rənginə görə
   ayrıca karta açılır — bazada məhsul birdir, kart virtualdır. 0 stoklu və
   ya merchant tərəfindən söndürülmüş rəng heç vaxt kart olmur. */
export function explodeColourCards(products) {
  const out = [];
  (products || []).forEach((product) => {
    const eligible = product?.display_colors_as_separate_products
      ? (product.colours || []).filter(
          (colour) => colour.image_url && colour.separate_card !== false && colour.in_stock,
        )
      : [];
    if (eligible.length > 0) {
      eligible.forEach((colour) => out.push({
        ...product,
        card_colour: colour,
        // Kart öz rənginin stokunu göstərir; limitsiz (backorder) hal null-dur.
        stock: colour.stock > 0 ? colour.stock : (colour.in_stock ? null : 0),
        __cardKey: `${product.id}:${colour.option_value_id}`,
      }));
    } else {
      out.push(product);
    }
  });
  return out;
}

export function normalizeProduct(product, index = 0) {
  // The list payload sends plain URLs; the detail payload sends image objects.
  const apiImages = (product?.images || [])
    .map((image) => (typeof image === "string" ? image : image?.url))
    .filter(isUsableImage);
  const primary = isUsableImage(product?.thumbnail_url) ? product.thumbnail_url : apiImages[0];
  const hover = apiImages.find((url) => url !== primary);
  const fallback = productImage(index);
  return {
    ...product,
    // No demo record is merged in any more. A card used to inherit the title,
    // photography and tags of a bundled placeholder whenever a field was
    // missing, which is how a real t-shirt ended up filed as "Xarici geyim".

    // thumbnail_url stays a plain string because the PDP reads it that way.
    // thumbnailSource carries the responsive descriptor when we fall back to
    // bundled photography, so the card can serve a 360px file to a phone.
    thumbnail_url: mediaUrl(primary) || fallback?.src || null,
    thumbnailSource: primary ? mediaUrl(primary) : fallback,
    // 720 pilləsi: hover şəkli orijinal ölçüdə gedirdi — böyük fayl yüklənənə
    // qədər əsas şəkil artıq solmuş olurdu və kart bir neçə saniyə boş
    // görünürdü («şəkil yoxa çıxır»). Kartın öz eni üçün 720 kifayətdir.
    hover_url: sizedMediaUrl(hover || product?.hover_url || null, 720),
    title: product?.title,
    occasion_tags: product?.occasion_tags || [],
  };
}

function isUsableImage(url) {
  return Boolean(url) && !String(url).includes("picsum.photos") && !String(url).includes("placehold.co");
}

export default function ProductCard({
  product,
  index = 0,
  // How wide the card actually renders, so the browser can pick a matching
  // source. The default describes the catalogue grid; a rail lays its cards out
  // differently and has to say so — see ProductRail.
  imageSizes = "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw",
  testId = "product-card",
  // Names the surface this card was clicked from, so `select_item` can be
  // attributed to a rail, a grid or a recommendation block rather than all
  // landing in one undifferentiated bucket.
  listName = "catalogue",
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const tryOnLauncher = useTryOnLauncher();
  const canHover = useHoverCapable();
  const item = normalizeProduct(product, index);
  // Colours that actually have a photograph — a swatch that changes nothing
  // when you press it is worse than no swatch.
  const colours = (product?.colours || []).filter((colour) => colour.image_url);
  // Virtual rəng kartı: rəng kilidlidir — swatch cərgəsi gizlənir, şəkil,
  // stok, ölçülər və try-on məhz bu rəngə bağlanır.
  const lockedColour = product?.card_colour || null;
  const [activeColour, setActiveColour] = useState(lockedColour);
  const [saved, setSaved] = useState(false);
  const [savingWardrobe, setSavingWardrobe] = useState(false);
  const shown = activeColour?.image_url ? mediaUrl(activeColour.image_url) : item.thumbnailSource;
  const title = pickLocale(item.title, locale);
  const cardTitle = lockedColour
    ? `${title} — ${pickLocale(lockedColour.label, locale) || lockedColour.value}`
    : title;
  const meta = [categoryLabel(item, locale, t), item.fit_type]
    .filter(Boolean)
    .slice(0, 2)
    .map((tag) => translateTag(tag, t))
    .join(" · ");
  const price = formatAZN(item.min_price, locale);
  const visibleColours = colours.slice(0, 4);
  const extraColourCount = Math.max(0, colours.length - visibleColours.length);
  // The list payload carries no variants — only an aggregate `stock` — so the
  // per-variant check never fired and a sold-out card kept its live button.
  const soldOut = item.variants?.length
    ? item.variants.every((variant) => Number(variant.stock || 0) <= 0)
    : item.stock != null && Number(item.stock) <= 0;
  // Bayraq QAPIDIR (aksesuar/ayaqqabıda söndürülür), şəkil isə xammal —
  // «və» olmalıdır: əvvəlki «və ya» ayaqqabılarda da düymə göstərirdi.
  const tryOnAvailable = Boolean(
    (item.try_on_ui ?? item.ai_try_on_enabled)
    && (
      item.ai_garment_asset
      || isUsableImage(product?.thumbnail_url)
      || (product?.images || []).some((image) => isUsableImage(typeof image === "string" ? image : image?.url))
    ),
  );
  // Endirim: compare_at doludursa köhnə qiymət xətlənir, faiz nişanı önə düşür.
  const onSale = Number(item.compare_at_minor || 0) > Number(item.min_price || 0);
  const wasPrice = onSale ? formatAZN(item.compare_at_minor, locale) : "";
  const badges = [
    onSale && item.discount_percent ? `−${item.discount_percent}%` : "",
    item.is_new_arrival ? t("product.badge.new", "Yeni") : "",
    item.is_bestseller ? t("product.badge.bestseller", "Bestseller") : "",
    item.limited_edition ? t("product.badge.limited", "Limited") : "",
  ].filter(Boolean).slice(0, 2);

  const toggleSave = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!authStore.getAccessToken()) {
      navigate(`/auth/login?next=/products/${item.handle}`);
      return;
    }
    if (saved || savingWardrobe) return;
    setSavingWardrobe(true);
    try {
      await api.post("/me/wardrobe", { product_id: item.id, variant_id: null });
      analytics.addToWishlist(item);
      setSaved(true);
    } catch {
      setSaved(false);
    } finally {
      setSavingWardrobe(false);
    }
  };

  const openTryOn = (event) => {
    event.preventDefault();
    event.stopPropagation();
    tryOnLauncher?.openTryOn(item, lockedColour ? { colourValueId: lockedColour.option_value_id } : {});
  };

  return (
    <Link
      to={`/products/${item.handle}${lockedColour ? `?color=${encodeURIComponent(lockedColour.value)}` : ""}`}
      data-testid={`${testId}-${index}`}
      className="adf-product-card group"
      onClick={() => analytics.selectItem(listName, item, index)}
    >
      <div className="adf-product-card-media">
        <AdfImage
          source={shown}
          alt={title}
          /* The first row is the LCP candidate; everything below the fold waits. */
          priority={index < 4}
          sizes={imageSizes}
          /* Şəffaflaşma YALNIZ arxada ikinci şəkil render olunanda: əks halda
             hover kartı boşaldırdı — ikinci foto yoxdursa (və ya rəng seçilib
             onun fotosu göstərilirsə) əsas şəkil yerində qalır. */
          className={`adf-product-card-image transition duration-500 ${
            canHover && item.hover_url && !activeColour ? "group-hover:opacity-0" : ""
          }`}
        />
        {/* A touch device can never trigger :hover, so this second image was
            pure waste — it doubled the requests on exactly the connections
            least able to afford them. The swap is also pointless when there is
            only one photograph, which is most of this catalogue. */}
        {canHover && item.hover_url && !activeColour && (
          <img
            src={item.hover_url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            width="720"
            height="960"
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition duration-500 group-hover:scale-[1.025] group-hover:opacity-100"
          />
        )}
        {/* This was a decorative span with aria-hidden: it looked exactly like a
            save control and did nothing at all. It writes to the wardrobe now,
            the same endpoint the product page uses. */}
        <button
          type="button"
          onClick={toggleSave}
          disabled={savingWardrobe}
          aria-pressed={saved}
          aria-label={saved
            ? t("product.saved", "Qarderobda")
            : t("product.save", "Qarderoba əlavə et")}
          title={saved
            ? t("product.saved", "Qarderobda")
            : t("product.save", "Qarderoba əlavə et")}
          data-testid={`card-save-${index}`}
          className={`adf-product-card-save ${saved ? "is-saved" : ""}`}
        >
          <Bookmark size={18} strokeWidth={saved ? 2 : 1.5} fill={saved ? "currentColor" : "none"} />
        </button>
        <div className="adf-product-card-badges">
          {soldOut || item.is_locked ? (
            <span className="adf-product-card-badge adf-product-card-badge--solid">{t("product.sold_out", "Satılıb")}</span>
          ) : (
            <>
              {badges.map((badge) => (
                <span key={badge} className="adf-product-card-badge">{badge}</span>
              ))}
            </>
          )}
        </div>
        {!soldOut && !item.is_locked && tryOnAvailable && (
          <TryOnBadge
            as="span"
            label={t("product.try_ai", "AI ilə yoxla")}
            role="button"
            tabIndex={0}
            variant="short"
            onClick={openTryOn}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") openTryOn(event);
            }}
            className="adf-product-card-tryon cursor-pointer"
          />
        )}
        {/* Ölçü/səbət hover paneli 2026-10-05 tapşırığı ilə çıxarıldı: hover-də
            «AI ilə yoxla» düyməsini örtürdü və müştəri ona klikləyə bilmirdi.
            Ölçü seçimi və «Səbətə at» indi yalnız məhsul səhifəsindədir; kart
            hover-də yalnız ikinci şəkli göstərir. */}
      </div>
      <div className="adf-product-card-info">
        <div className="adf-product-card-line">
          {meta && <span className="adf-product-card-meta">{meta}</span>}
          {price && (
            <span className="adf-product-card-price">
              {wasPrice && <s className="adf-product-card-price-was">{wasPrice}</s>}
              {price}
            </span>
          )}
        </div>
        <h3 className="adf-product-card-title">{cardTitle}</h3>
        {/* A piece that comes in four colours used to look like one product in
            one colour until you opened it. The card is a link, so a swatch has
            to stop the click from navigating. */}
        {!lockedColour && colours.length > 1 && (
          <div className="adf-card-colour-row" aria-label={t("product.colours", "Rənglər")}>
            {visibleColours.map((colour) => {
              const selected = activeColour?.option_value_id === colour.option_value_id;
              const choose = (event) => {
                event.preventDefault();
                event.stopPropagation();
                setActiveColour(selected ? null : colour);
              };
              return (
                <span
                  key={colour.option_value_id}
                  role="button"
                  tabIndex={0}
                  aria-label={pickLocale(colour.label, locale) || colour.value}
                  aria-pressed={selected}
                  title={pickLocale(colour.label, locale) || colour.value}
                  data-testid={`card-colour-${index}`}
                  onClick={choose}
                  onMouseEnter={() => setActiveColour(colour)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") choose(event);
                  }}
                  className={`adf-card-swatch ${selected ? "is-selected" : ""}`}
                >
                  <span className="adf-card-swatch-fill" style={{ background: colour.swatch_hex }} />
                </span>
              );
            })}
            {extraColourCount > 0 && (
              <span className="adf-card-swatch-more" aria-label={`${extraColourCount} rəng daha`}>
                +{extraColourCount}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
